import React, { useMemo, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import '../../components/DesignSystem/designSystem.css';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import {
  CarouselShowcase,
  CrystalModal,
  CrystalForm,
  CrystalTable,
  CrystalTableColumn,
  DesignMode,
  DesignSurface,
  FeedbackShowcase,
  GiveawayShowcase,
  GlassButton,
  LayoutRulesShowcase,
  NavigationShowcase,
  PackCardsShowcase,
  SiteButton,
  SiteCountdownTimer,
  SpaceShaderPanel,
  TrustBar,
  TypographyShowcase,
  UtilityPatternsShowcase,
  createDesignTheme,
  getDesignTokens
} from '../../components/DesignSystem';

const futureDate = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000 + 4219000);

const buttonBackgroundOptions = [
  { name: 'White', value: '#ffffff' },
  { name: 'Ice', value: '#eef6ff' },
  { name: 'Silver', value: '#cbd5e1' },
  {
    name: 'Crystal',
    value:
      'linear-gradient(135deg, rgba(65,200,196,0.26), rgba(137,92,246,0.22))'
  },
  { name: 'Slate', value: '#475569' },
  { name: 'Navy', value: '#08113f' },
  { name: 'Black', value: '#05070c' }
];

const topNavItems = [
  { label: 'Components', href: '#buttons' },
  { label: 'Typography', href: '#typography' },
  { label: 'Cards', href: '#cards' },
  { label: 'Giveaway', href: '#giveaway' },
  { label: 'Forms', href: '#forms' },
  { label: 'Tables', href: '#data-table' },
  { label: 'Patterns', href: '#utility-patterns' },
  { label: 'Tokens', href: '#color-tokens' }
];

const componentLineup = [
  { label: 'Buttons', detail: 'Glass CTA', icon: <BoltRoundedIcon /> },
  { label: 'Cards', detail: 'Packs', icon: <LayersRoundedIcon /> },
  { label: 'Navigation', detail: 'Drawers', icon: <GridViewRoundedIcon /> },
  { label: 'Forms', detail: 'Inputs', icon: <CheckRoundedIcon /> },
  { label: 'Data', detail: 'Tables', icon: <AutoAwesomeRoundedIcon /> },
  { label: 'Patterns', detail: 'Flows', icon: <LockRoundedIcon /> }
];

type MockTableRow = {
  asset: string;
  type: string;
  floor: number;
  volume: number;
  status: string;
};

const mockTableRows: MockTableRow[] = [
  {
    asset: 'Nebula Keycard',
    type: 'NFT',
    floor: 124,
    volume: 8420,
    status: 'Listed'
  },
  {
    asset: 'Crystal Vault',
    type: 'Pack',
    floor: 89,
    volume: 5130,
    status: 'Active'
  },
  {
    asset: 'Quantum Pass',
    type: 'Access',
    floor: 210,
    volume: 12880,
    status: 'Featured'
  },
  {
    asset: 'Void Fragment',
    type: 'Token',
    floor: 36,
    volume: 2190,
    status: 'Paused'
  }
];

const tableColumns = (): CrystalTableColumn<MockTableRow>[] => [
  { key: 'asset', label: 'Asset' },
  { key: 'type', label: 'Type' },
  {
    key: 'floor',
    label: 'Floor',
    align: 'right',
    render: (row) => `$${row.floor.toLocaleString()}`
  },
  {
    key: 'volume',
    label: 'Volume',
    align: 'right',
    render: (row) => `$${row.volume.toLocaleString()}`
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    render: (row) => row.status
  }
];

const Section = ({
  title,
  description,
  mode,
  tone = 'light',
  children
}: {
  title: string;
  description: string;
  mode: DesignMode;
  tone?: 'light' | 'dark';
  children: React.ReactNode;
}) => {
  const tokens = getDesignTokens(mode);
  const isDark = tone === 'dark';

  return (
    <Box
      component="section"
      id={title.toLowerCase().replace(/\s+/g, '-')}
      sx={{
        position: 'relative',
        display: 'grid',
        gap: 2.5,
        py: isDark ? { xs: 4, md: 6 } : 0,
        isolation: 'isolate',
        '&::before': isDark
          ? {
              content: '""',
              position: 'absolute',
              zIndex: -1,
              top: 0,
              bottom: 0,
              left: '50%',
              width: '100vw',
              transform: 'translateX(-50%)',
              background:
                'linear-gradient(135deg, #05070c 0%, #111827 48%, #0a2a32 100%)'
            }
          : undefined
      }}
    >
      <Box>
        <Typography
          component="h2"
          sx={{
            color: isDark ? '#f8fafc' : tokens.colors.text,
            fontSize: { xs: 38, md: 64 },
            fontWeight: 900,
            lineHeight: 1.02,
            letterSpacing: '-0.03em'
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 0.9,
            color: isDark ? 'rgba(248,250,252,0.68)' : tokens.colors.textMuted,
            fontSize: { xs: 16, md: 18 },
            lineHeight: 1.7,
            maxWidth: 820
          }}
        >
          {description}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

const DesignSystemPage = () => {
  const [mode, setMode] = useState<DesignMode>('light');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const [isTableSortable, setIsTableSortable] = useState(true);
  const [buttonBackground, setButtonBackground] = useState(
    buttonBackgroundOptions[0].value
  );
  const [buttonTextColor, setButtonTextColor] = useState<'black' | 'white'>(
    'black'
  );
  const tokens = getDesignTokens(mode);
  const designTheme = useMemo(() => createDesignTheme(mode), [mode]);
  const mockColumns = useMemo(() => tableColumns(), []);
  const swatches = useMemo(
    () => [
      ['Page', tokens.colors.page],
      ['Surface', tokens.colors.surface],
      ['Raised', tokens.colors.surfaceRaised],
      ['Teal', tokens.colors.teal],
      ['Green', tokens.colors.green],
      ['Gold', tokens.colors.gold],
      ['Red', tokens.colors.red]
    ],
    [tokens]
  );

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextMode: DesignMode | null
  ) => {
    if (nextMode) {
      setMode(nextMode);
    }
  };

  return (
    <ThemeProvider theme={designTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background:
            mode === 'light'
              ? 'linear-gradient(180deg, #ffffff 0%, #f5f5f7 42%, #eceff3 100%)'
              : `linear-gradient(180deg, ${tokens.colors.page} 0%, ${tokens.colors.pageSoft} 100%)`,
          color: tokens.colors.text,
          px: { xs: 0, md: 0 },
          py: 0
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderBottom:
              mode === 'light'
                ? '1px solid rgba(15,23,42,0.08)'
                : '1px solid rgba(255,255,255,0.08)',
            background:
              mode === 'light' ? 'rgba(251,251,253,0.82)' : 'rgba(5,7,12,0.72)',
            backdropFilter: 'saturate(180%) blur(18px)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1180,
              mx: 'auto',
              minHeight: { xs: 64, md: 76 },
              px: { xs: 2, md: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Typography
              component="a"
              href="/design"
              sx={{
                color: tokens.colors.text,
                textDecoration: 'none',
                fontFamily: 'var(--design-font-display)',
                fontSize: { xs: 19, md: 24 },
                fontWeight: 900,
                letterSpacing: 0
              }}
            >
              INCRYPT
            </Typography>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 3.6
              }}
            >
              {topNavItems.map((item) => (
                <Typography
                  key={item.label}
                  component="a"
                  href={item.href}
                  sx={{
                    color: tokens.colors.textMuted,
                    fontSize: 15,
                    fontWeight: 750,
                    textDecoration: 'none',
                    '&:hover': { color: tokens.colors.text }
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
            <Typography
              sx={{
                color: tokens.colors.textMuted,
                fontSize: 14,
                fontWeight: 800
              }}
            >
              Design Lab
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            py: 1.5,
            px: 2,
            background:
              mode === 'light'
                ? 'rgba(245,245,247,0.96)'
                : 'rgba(255,255,255,0.04)',
            color: tokens.colors.text,
            fontSize: { xs: 13, md: 14 },
            fontWeight: 650
          }}
        >
          Building a reusable INCRYPT interface library.{' '}
          <Box
            component="a"
            href="/design/giveaway"
            sx={{
              color: tokens.colors.tealDeep,
              textDecoration: 'none',
              fontWeight: 800
            }}
          >
            Preview giveaway redesign &gt;
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: tokens.layout.contentMax,
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 4, md: 6 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: { xs: 3, md: 5 }
            }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={handleModeChange}
              sx={{
                p: 0.4,
                border: `1px solid ${tokens.colors.border}`,
                borderRadius: tokens.radius.lg,
                background:
                  mode === 'light'
                    ? 'rgba(255,255,255,0.72)'
                    : 'rgba(255,255,255,0.06)',
                '& .MuiToggleButton-root': {
                  minWidth: 72,
                  minHeight: 34,
                  border: 0,
                  borderRadius: tokens.radius.md,
                  color: tokens.colors.textMuted,
                  fontWeight: 900,
                  textTransform: 'none',
                  letterSpacing: 0,
                  '&.Mui-selected': {
                    color: mode === 'light' ? '#041012' : tokens.colors.text,
                    background:
                      mode === 'light'
                        ? 'rgba(65,200,196,0.22)'
                        : 'rgba(65,200,196,0.16)'
                  },
                  '&.Mui-selected:hover, &:hover': {
                    background:
                      mode === 'light'
                        ? 'rgba(65,200,196,0.18)'
                        : 'rgba(65,200,196,0.14)'
                  }
                }
              }}
            >
              <ToggleButton value="light">Light</ToggleButton>
              <ToggleButton value="dark">Dark</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              position: 'relative',
              isolation: 'isolate',
              minHeight: { xs: 520, md: 620 },
              display: 'grid',
              alignItems: 'center',
              justifyItems: 'center',
              textAlign: 'center',
              mb: { xs: 5, md: 7 },
              borderRadius: { xs: 3, md: 3.5 },
              overflow: 'hidden',
              background:
                mode === 'light'
                  ? 'radial-gradient(46% 40% at 16% 8%, rgba(65,200,196,0.18), transparent 60%), radial-gradient(44% 40% at 86% 14%, rgba(139,92,246,0.16), transparent 60%), radial-gradient(56% 52% at 74% 104%, rgba(243,191,77,0.13), transparent 62%), linear-gradient(180deg, #ffffff 0%, #f6f8fb 100%)'
                  : 'radial-gradient(46% 40% at 16% 8%, rgba(65,200,196,0.22), transparent 60%), radial-gradient(44% 40% at 86% 14%, rgba(139,92,246,0.26), transparent 60%), radial-gradient(56% 52% at 74% 104%, rgba(243,191,77,0.12), transparent 62%), linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
              border:
                mode === 'light'
                  ? '1px solid rgba(15,23,42,0.06)'
                  : '1px solid rgba(255,255,255,0.08)',
              boxShadow:
                mode === 'light'
                  ? 'inset 0 1px 0 rgba(255,255,255,0.9), 0 30px 80px rgba(15,23,42,0.06)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.08), 0 30px 80px rgba(0,0,0,0.4)',
              // Fine starfield that fades away from the centre so it never
              // competes with the headline — the "slight space" atmosphere.
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: 0,
                pointerEvents: 'none',
                backgroundImage:
                  mode === 'light'
                    ? 'radial-gradient(1.6px 1.6px at 12% 24%, rgba(15,23,42,0.24), transparent 60%), radial-gradient(1.3px 1.3px at 27% 66%, rgba(15,23,42,0.16), transparent 60%), radial-gradient(1.7px 1.7px at 83% 30%, rgba(15,23,42,0.2), transparent 60%), radial-gradient(1.2px 1.2px at 68% 82%, rgba(15,23,42,0.14), transparent 60%), radial-gradient(1.4px 1.4px at 92% 60%, rgba(15,23,42,0.16), transparent 60%), radial-gradient(1.2px 1.2px at 45% 12%, rgba(15,23,42,0.14), transparent 60%), radial-gradient(1.1px 1.1px at 6% 52%, rgba(15,23,42,0.12), transparent 60%)'
                    : 'radial-gradient(1.6px 1.6px at 12% 24%, rgba(255,255,255,0.6), transparent 60%), radial-gradient(1.3px 1.3px at 27% 66%, rgba(255,255,255,0.4), transparent 60%), radial-gradient(1.7px 1.7px at 83% 30%, rgba(255,255,255,0.55), transparent 60%), radial-gradient(1.2px 1.2px at 68% 82%, rgba(255,255,255,0.35), transparent 60%), radial-gradient(1.4px 1.4px at 92% 60%, rgba(255,255,255,0.45), transparent 60%), radial-gradient(1.2px 1.2px at 45% 12%, rgba(255,255,255,0.4), transparent 60%), radial-gradient(1.1px 1.1px at 6% 52%, rgba(255,255,255,0.3), transparent 60%)',
                maskImage:
                  'radial-gradient(120% 86% at 50% 44%, transparent 30%, #000 82%)',
                WebkitMaskImage:
                  'radial-gradient(120% 86% at 50% 44%, transparent 30%, #000 82%)'
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                maxWidth: 940,
                px: { xs: 2, md: 4 }
              }}
            >
              <Typography
                sx={{
                  color: tokens.colors.tealDeep,
                  fontSize: 14,
                  fontWeight: 850,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase'
                }}
              >
                INCRYPT Design System
              </Typography>
              <Typography
                component="h1"
                sx={{
                  mt: 2,
                  color: tokens.colors.text,
                  fontSize: { xs: 56, sm: 88, md: 124 },
                  fontWeight: 900,
                  lineHeight: 0.9,
                  letterSpacing: '-0.055em'
                }}
              >
                Designed for every screen.
              </Typography>
              <Typography
                sx={{
                  mt: 3,
                  color: tokens.colors.textMuted,
                  fontSize: { xs: 21, md: 30 },
                  lineHeight: 1.35,
                  maxWidth: 780,
                  mx: 'auto',
                  fontWeight: 650
                }}
              >
                A crystal-glass component system with Apple-like restraint:
                spacious, precise, readable, and broken up with darker product
                moments.
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1.2,
                  flexWrap: 'wrap'
                }}
              >
                <SiteButton mode={mode} startIcon={<BoltRoundedIcon />}>
                  Browse Components
                </SiteButton>
                <SiteButton
                  mode={mode}
                  tone="secondary"
                  startIcon={<LockRoundedIcon />}
                >
                  Design Rules
                </SiteButton>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              mb: { xs: 5, md: 8 },
              py: { xs: 2, md: 3 },
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' }
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 760, md: 'auto' },
                display: 'grid',
                gridTemplateColumns: 'repeat(6, minmax(110px, 1fr))',
                gap: 2,
                alignItems: 'start'
              }}
            >
              {componentLineup.map((item) => (
                <Box key={item.label} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      mx: 'auto',
                      width: 76,
                      height: 58,
                      display: 'grid',
                      placeItems: 'center',
                      color: tokens.colors.text,
                      '& svg': { fontSize: 34 }
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: tokens.colors.text,
                      fontSize: 14,
                      fontWeight: 850
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: tokens.colors.tealDeep,
                      fontSize: 12,
                      fontWeight: 750
                    }}
                  >
                    {item.detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: { xs: 5, md: 6 } }}>
            <Section
              mode={mode}
              title="Typography"
              description="The display stack carries hero text and big headings. The body stack handles readable UI text, forms, cards, and longer copy."
            >
              <TypographyShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Trust Signals"
              description="Use one structured trust bar for short proof points, with calm dividers and a single success accent."
            >
              <TrustBar
                mode={mode}
                items={[
                  'Fully compliant',
                  'Australian owned',
                  'Publicly announced',
                  'Rewards included',
                  'Fair and transparent'
                ]}
              />
            </Section>

            <Section
              mode={mode}
              title="Layout Rules"
              description="Shared max-width and spacing rules for the rebuild. Use these values as the source of truth before inventing new layout gaps."
            >
              <LayoutRulesShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Space Shader"
              description="A space-like animated shader panel for crystal-glass hero moments and brand-led sections."
              tone="dark"
            >
              <DesignSurface
                mode={mode}
                elevated
                sx={{ p: { xs: 1, sm: 1.4 } }}
              >
                <SpaceShaderPanel mode={mode} word="INCRYPT" />
              </DesignSurface>
            </Section>

            <Section
              mode={mode}
              title="Navigation"
              description="A desktop left-side navigation direction with a mobile menu that slides in from the right edge of the screen."
            >
              <NavigationShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Cards"
              description="Pack cards for one-off purchases, subscriptions, and premium offers, with the same crystal-glass surface language."
              tone="dark"
            >
              <PackCardsShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Carousels"
              description="Horizontal product rails for packs, prizes, and featured items. Cards keep the same reusable pack structure."
            >
              <CarouselShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Giveaway"
              description="The signature giveaway pattern, composed entirely from system primitives — big prize, live countdown, proof stats, a trust bar, and a bold call to action. High energy, but still clean and light-first."
            >
              <GiveawayShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Buttons"
              description="Two tiers: standard pills for everyday light UI, and glass buttons for dark, hero, and premium moments. Primary actions should feel energetic and obvious; secondary and quiet actions stay readable without competing for attention."
            >
              <DesignSurface
                mode={mode}
                sx={{
                  p: { xs: 2, sm: 3 },
                  background: buttonBackground
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1.5,
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9 }}>
                    {buttonBackgroundOptions.map((option) => (
                      <Box
                        key={option.name}
                        component="button"
                        type="button"
                        aria-label={`Use ${option.name} button background`}
                        onClick={() => setButtonBackground(option.value)}
                        sx={{
                          width: 34,
                          height: 34,
                          p: 0,
                          borderRadius: 999,
                          border:
                            buttonBackground === option.value
                              ? `2px solid ${buttonTextColor === 'black' ? '#0f172a' : '#ffffff'}`
                              : '1px solid rgba(15,23,42,0.16)',
                          background: option.value,
                          boxShadow:
                            buttonBackground === option.value
                              ? '0 0 0 3px rgba(65,200,196,0.2)'
                              : 'inset 0 1px 0 rgba(255,255,255,0.5)',
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </Box>

                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={buttonTextColor}
                    onChange={(_event, value: 'black' | 'white' | null) => {
                      if (value) setButtonTextColor(value);
                    }}
                    sx={{
                      p: 0.35,
                      borderRadius: 999,
                      border: '1px solid rgba(15,23,42,0.12)',
                      background: 'rgba(255,255,255,0.42)',
                      backdropFilter: 'blur(14px)',
                      '& .MuiToggleButton-root': {
                        minWidth: 72,
                        minHeight: 32,
                        border: 0,
                        borderRadius: 999,
                        color: '#0f172a',
                        fontWeight: 900,
                        textTransform: 'none',
                        letterSpacing: 0,
                        '&.Mui-selected': {
                          background:
                            buttonTextColor === 'black'
                              ? 'rgba(15,23,42,0.1)'
                              : 'rgba(15,23,42,0.82)',
                          color:
                            buttonTextColor === 'black' ? '#0f172a' : '#fff'
                        }
                      }
                    }}
                  >
                    <ToggleButton value="black">Black</ToggleButton>
                    <ToggleButton value="white">White</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                  <SiteButton
                    mode={mode}
                    tone="success"
                    size="large"
                    startIcon={<BoltRoundedIcon />}
                    sx={{
                      color: buttonTextColor === 'black' ? '#0f172a' : '#fff'
                    }}
                  >
                    Continue Purchase
                  </SiteButton>
                  <SiteButton
                    mode={mode}
                    tone="secondary"
                    startIcon={<CheckRoundedIcon />}
                    sx={{
                      color: buttonTextColor === 'black' ? '#0f172a' : '#fff'
                    }}
                  >
                    Secondary Action
                  </SiteButton>
                  <SiteButton
                    mode={mode}
                    tone="quiet"
                    sx={{
                      color: buttonTextColor === 'black' ? '#0f172a' : '#fff'
                    }}
                  >
                    Quiet Action
                  </SiteButton>
                  <SiteButton
                    mode={mode}
                    tone="danger"
                    sx={{
                      color: buttonTextColor === 'black' ? '#8a1f1f' : '#fff'
                    }}
                  >
                    Danger Action
                  </SiteButton>
                  <SiteButton
                    mode={mode}
                    disabled
                    sx={{
                      color:
                        buttonTextColor === 'black'
                          ? 'rgba(15,23,42,0.38)'
                          : 'rgba(255,255,255,0.5)'
                    }}
                  >
                    Disabled
                  </SiteButton>
                </Box>
              </DesignSurface>

              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  mt: 2,
                  p: { xs: 2.5, sm: 3.5 },
                  borderRadius: tokens.radius.lg,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background:
                    'radial-gradient(120% 160% at 12% 0%, #1e3a5f 0%, #0b1020 42%, #05070c 72%, #123032 100%)',
                  boxShadow: '0 22px 58px rgba(15,23,42,0.22)',
                  // Blurry light blobs behind the buttons so the glass has
                  // something to refract
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(220px 140px at 22% 78%, rgba(94,234,212,0.28), transparent 70%), radial-gradient(260px 160px at 68% 18%, rgba(137,92,246,0.3), transparent 70%), radial-gradient(200px 130px at 88% 82%, rgba(250,204,21,0.2), transparent 70%)',
                    filter: 'blur(6px)',
                    pointerEvents: 'none'
                  }
                }}
              >
                <Typography
                  sx={{
                    mb: 0.5,
                    position: 'relative',
                    color: '#ffffff',
                    fontFamily: 'var(--design-font-display)',
                    fontSize: { xs: 22, sm: 28 },
                    fontWeight: 850,
                    lineHeight: 1.1
                  }}
                >
                  Glass buttons
                </Typography>
                <Typography
                  sx={{
                    mb: 2.2,
                    position: 'relative',
                    color: 'rgba(248,250,252,0.62)',
                    fontSize: 14,
                    lineHeight: 1.6,
                    maxWidth: 620
                  }}
                >
                  A more physical take on glass: curved specular highlight, rim
                  lighting on the edges, deep refraction blur, and a sheen that
                  sweeps across on hover.
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.4
                  }}
                >
                  <GlassButton
                    mode={mode}
                    tone="aurora"
                    size="large"
                    startIcon={<BoltRoundedIcon />}
                  >
                    Continue Purchase
                  </GlassButton>
                  <GlassButton
                    mode={mode}
                    tone="crystal"
                    startIcon={<CheckRoundedIcon />}
                  >
                    Secondary Action
                  </GlassButton>
                  <GlassButton mode={mode} tone="gold">
                    Premium Action
                  </GlassButton>
                  <GlassButton mode={mode} tone="ruby">
                    Danger Action
                  </GlassButton>
                  <GlassButton mode={mode} tone="obsidian">
                    Quiet Action
                  </GlassButton>
                  <GlassButton mode={mode} disabled>
                    Disabled
                  </GlassButton>
                </Box>
              </Box>
            </Section>

            <Section
              mode={mode}
              title="Timers"
              description="Countdowns use tabular numbers, fixed sizing, and strong contrast so they do not jump around while ticking."
            >
              <Box
                sx={{
                  display: 'grid',
                  gap: 2
                }}
              >
                <DesignSurface
                  elevated
                  mode={mode}
                  sx={{ p: { xs: 2, sm: 3 } }}
                >
                  <SiteCountdownTimer
                    mode={mode}
                    targetDate={futureDate}
                    size="large"
                    label="Next weekly Bitcoin draw closes in"
                  />
                </DesignSurface>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
                    gap: 2
                  }}
                >
                  <DesignSurface
                    elevated
                    mode={mode}
                    sx={{ p: { xs: 2, sm: 3 } }}
                  >
                    <SiteCountdownTimer
                      mode={mode}
                      targetDate={futureDate}
                      label="Next weekly draw"
                    />
                  </DesignSurface>
                  <DesignSurface mode={mode} sx={{ p: { xs: 2, sm: 3 } }}>
                    <SiteCountdownTimer
                      mode={mode}
                      targetDate={futureDate}
                      compact
                      fieldsToShow={['hours', 'minutes', 'seconds']}
                      label="Flash offer"
                    />
                  </DesignSurface>
                </Box>
              </Box>
            </Section>

            <Section
              mode={mode}
              title="Modal"
              description="Desktop modals use a wide centered glass panel. On mobile, the same component becomes a full-screen focused flow."
            >
              <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: tokens.colors.text,
                        fontSize: 20,
                        fontWeight: 900
                      }}
                    >
                      Glass checkout-style modal
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        color: tokens.colors.textMuted,
                        fontSize: 14,
                        lineHeight: 1.6,
                        maxWidth: 560
                      }}
                    >
                      Use this pattern for focused decisions, purchases, account
                      flows, or compact forms.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <SiteButton
                      mode={mode}
                      onClick={() => setIsModalOpen(true)}
                    >
                      Open Modal
                    </SiteButton>
                    <SiteButton
                      mode={mode}
                      tone="secondary"
                      onClick={() => setIsLockedModalOpen(true)}
                    >
                      No X Modal
                    </SiteButton>
                  </Box>
                </Box>
              </DesignSurface>
              <CrystalModal
                open={isModalOpen}
                mode={mode}
                eyebrow="Crystal Modal"
                title="Confirm your next action."
                description="This modal keeps the glass/crystal language while giving users a clear, focused space to make a decision."
                primaryAction="Continue"
                secondaryAction="Not now"
                onClose={() => setIsModalOpen(false)}
              >
                <DesignSurface
                  mode={mode}
                  sx={{
                    p: 2,
                    borderRadius: tokens.radius.lg,
                    boxShadow: 'none'
                  }}
                >
                  <Typography
                    sx={{
                      color: tokens.colors.textMuted,
                      fontSize: 14,
                      lineHeight: 1.65
                    }}
                  >
                    This content area can hold a short form, confirmation
                    details, pricing summary, or important account message.
                  </Typography>
                </DesignSurface>
              </CrystalModal>
              <CrystalModal
                open={isLockedModalOpen}
                mode={mode}
                eyebrow="Locked Modal"
                title="No top-right close button."
                description="This version disables the X button, backdrop click, and escape key close. Users must choose an explicit action."
                primaryAction="I understand"
                secondaryAction="Close"
                showCloseButton={false}
                closeOnBackdropClick={false}
                closeOnEscapeKey={false}
                onClose={() => setIsLockedModalOpen(false)}
              />
            </Section>

            <Section
              mode={mode}
              title="Data Table"
              description="A glass table for marketplace, dashboard, and admin views. Sorting can be enabled or disabled per table."
            >
              <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mb: 2.5
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: tokens.colors.text,
                        fontSize: 20,
                        fontWeight: 900
                      }}
                    >
                      Marketplace table
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={isTableSortable ? 'sortable' : 'static'}
                    onChange={(_event, value: 'sortable' | 'static' | null) => {
                      if (value) setIsTableSortable(value === 'sortable');
                    }}
                    sx={{
                      p: 0.35,
                      borderRadius: 999,
                      border: `1px solid ${tokens.colors.border}`,
                      background:
                        mode === 'light'
                          ? 'rgba(255,255,255,0.52)'
                          : 'rgba(255,255,255,0.06)',
                      backdropFilter: 'blur(14px)',
                      '& .MuiToggleButton-root': {
                        minWidth: 92,
                        minHeight: 32,
                        border: 0,
                        borderRadius: 999,
                        color: tokens.colors.textMuted,
                        fontWeight: 900,
                        textTransform: 'none',
                        letterSpacing: 0,
                        '&.Mui-selected': {
                          background:
                            mode === 'light'
                              ? 'rgba(65,200,196,0.18)'
                              : 'rgba(65,200,196,0.16)',
                          color: tokens.colors.text
                        }
                      }
                    }}
                  >
                    <ToggleButton value="sortable">Sortable</ToggleButton>
                    <ToggleButton value="static">No Sort</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <CrystalTable
                  mode={mode}
                  columns={mockColumns}
                  rows={mockTableRows}
                  sortable={isTableSortable}
                />
              </DesignSurface>
            </Section>

            <Section
              mode={mode}
              title="Forms"
              description="Form controls for account, checkout, settings, and onboarding flows, including validation feedback."
            >
              <CrystalForm mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Utility Patterns"
              description="Marketplace and admin patterns for search, filters, pagination, tooltips, popovers, empty states, steppers, and confirmations."
              tone="dark"
            >
              <UtilityPatternsShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Feedback And Loading"
              description="Alerts, loading bars, and skeleton states for async product moments."
            >
              <FeedbackShowcase mode={mode} />
            </Section>

            <Section
              mode={mode}
              title="Surfaces And Labels"
              description="Use surfaces for repeated content, modals, dashboards, and focused tools. Labels should be short and functional."
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 2
                }}
              >
                {[
                  [
                    'Standard Surface',
                    'For everyday panels and grouped controls.'
                  ],
                  ['Raised Surface', 'For high-priority modules or modals.'],
                  ['Compact Surface', 'For dense dashboard information.']
                ].map(([title, copy], index) => (
                  <DesignSurface
                    key={title}
                    elevated={index === 1}
                    mode={mode}
                    sx={{ p: 2.5 }}
                  >
                    <Typography
                      sx={{
                        color: tokens.colors.text,
                        fontSize: 22,
                        fontWeight: 900
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1,
                        color: tokens.colors.textMuted,
                        fontSize: 14,
                        lineHeight: 1.6
                      }}
                    >
                      {copy}
                    </Typography>
                  </DesignSurface>
                ))}
              </Box>
            </Section>

            <Section
              mode={mode}
              title="Color Tokens"
              description="Keep the palette tight: clear light foundations first, with teal for system energy and gold for prize or premium moments."
              tone="dark"
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))',
                    lg: 'repeat(7, minmax(0, 1fr))'
                  },
                  gap: 1.5
                }}
              >
                {swatches.map(([name, color]) => (
                  <DesignSurface key={name} mode={mode} sx={{ p: 1.4 }}>
                    <Box
                      sx={{
                        height: 64,
                        borderRadius: tokens.radius.md,
                        background: color,
                        border: `1px solid ${tokens.colors.border}`
                      }}
                    />
                    <Typography sx={{ mt: 1.1, fontSize: 13, fontWeight: 900 }}>
                      {name}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        color: tokens.colors.textMuted,
                        fontSize: 12
                      }}
                    >
                      {color}
                    </Typography>
                  </DesignSurface>
                ))}
              </Box>
            </Section>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DesignSystemPage;
